/**
 * ProjectList Component
 * Displays list of projects with filtering and sorting
 */

import React, { useState, useEffect } from 'react';
import { projectApi } from '../services/api';

interface Project {
  id: number;
  project_number: string;
  name: string;
  client_id?: number;
  client_name?: string;
  status: string;
  progress: number;
  priority: string;
  due_date: string | null;
  created_at: string;
}

interface ProjectListProps {
  onNewProject?: () => void;
  onView?: (id: number) => void;
}

const ProjectList: React.FC<ProjectListProps> = ({ onNewProject, onView }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    status: 'all',
    priority: 'all',
    search: ''
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchProjects();
  }, [filter, sortBy, sortOrder]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params: { status?: string } = {};
      if (filter.status !== 'all') {
        params.status = filter.status;
      }
      const response = await projectApi.list(params);
      if (response.success && response.data) {
        setProjects(response.data.projects || []);
      } else {
        console.error('Failed to fetch projects:', response.error);
        setProjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    if (filter.status !== 'all' && project.status !== filter.status) return false;
    if (filter.priority !== 'all' && project.priority !== filter.priority) return false;
    if (filter.search) {
      const search = filter.search.toLowerCase();
      if (!project.name.toLowerCase().includes(search) && 
          !project.project_number.toLowerCase().includes(search) &&
          !project.client_name?.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'intake': return 'var(--secondary)';
      case 'queued': return 'var(--info, #06b6d4)';
      case 'in_progress': return 'var(--primary)';
      case 'review': return 'var(--warning)';
      case 'completed': return 'var(--success)';
      default: return 'var(--secondary)';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'var(--danger)';
      case 'high': return 'var(--warning)';
      case 'normal': return 'var(--secondary)';
      case 'low': return 'var(--success)';
      default: return 'var(--secondary)';
    }
  };

  return (
    <div className="project-list">
      {/* Header */}
      <div className="list-header">
        <h2>Projects</h2>
        <button className="btn primary" onClick={() => onNewProject && onNewProject()}>+ New Project</button>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Search projects..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          />
        </div>

        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="all">All Status</option>
          <option value="intake">Intake</option>
          <option value="queued">Queued</option>
          <option value="in_progress">In Progress</option>
          <option value="review">Review</option>
          <option value="completed">Completed</option>
        </select>

        <select
          value={filter.priority}
          onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
        >
          <option value="all">All Priority</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="created_at">Date Created</option>
          <option value="due_date">Due Date</option>
          <option value="progress">Progress</option>
          <option value="name">Name</option>
        </select>
      </div>

      {/* Stats */}
      <div className="list-stats">
        <span>{filteredProjects.length} projects</span>
        <span>•</span>
        <span>{filteredProjects.filter(p => p.status === 'in_progress').length} in progress</span>
        <span>•</span>
        <span>{filteredProjects.filter(p => p.priority === 'urgent' || p.priority === 'high').length} high priority</span>
      </div>

      {/* Project Table */}
      {loading ? (
        <div className="loading">Loading projects...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Client</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Priority</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProjects.map(project => (
              <tr key={project.id}>
                <td>
                  <div className="project-cell">
                    <span className="project-number">{project.project_number}</span>
                    <span className="project-name">{project.name}</span>
                  </div>
                </td>
                <td>{project.client_name || '-'}</td>
                <td>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: `${getStatusColor(project.status)}20`, color: getStatusColor(project.status) }}
                  >
                    {project.status.replace('_', ' ')}
                  </span>
                </td>
                <td>
                  <div className="progress-cell">
                    <div className="mini-progress">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${project.progress}%`, backgroundColor: getStatusColor(project.status) }}
                      ></div>
                    </div>
                    <span>{project.progress}%</span>
                  </div>
                </td>
                <td>
                  <span 
                    className="priority-badge"
                    style={{ backgroundColor: `${getPriorityColor(project.priority)}20`, color: getPriorityColor(project.priority) }}
                  >
                    {project.priority}
                  </span>
                </td>
                <td>{project.due_date ? new Date(project.due_date).toLocaleDateString() : '-'}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn small" onClick={() => onView && onView(project.id)}>View</button>
                    <button className="btn small icon" disabled title="Coming soon">⋮</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <style>{`
        .project-list {
          padding: 0;
        }

        .list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .list-header h2 {
          margin: 0;
        }

        .filters {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .search-box {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--bg-tertiary, #334155);
          padding: 8px 16px;
          border-radius: 8px;
          flex: 1;
          min-width: 200px;
        }

        .search-box svg {
          color: var(--text-secondary);
        }

        .search-box input {
          background: none;
          border: none;
          color: var(--text-primary);
          flex: 1;
          outline: none;
        }

        .filters select {
          padding: 8px 16px;
          background: var(--bg-tertiary, #334155);
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px;
          color: var(--text-primary);
        }

        .list-stats {
          display: flex;
          gap: 8px;
          font-size: 14px;
          color: var(--text-secondary);
          margin-bottom: 16px;
        }

        .project-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .project-number {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .project-name {
          font-weight: 500;
        }

        .status-badge, .priority-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .progress-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mini-progress {
          width: 60px;
          height: 6px;
          background: var(--bg-tertiary, #334155);
          border-radius: 3px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.3s;
        }

        .action-buttons {
          display: flex;
          gap: 4px;
        }

        .btn.icon {
          padding: 4px 8px;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary);
        }
      `}</style>
    </div>
  );
};

export default ProjectList;
